
import {
  Injectable
}                                from '@angular/core';
import { HttpClient, HttpHeaders }                                from '@angular/common/http';
import {
  BehaviorSubject, defer, EMPTY, from, mergeAll,
  Observable, ObservedValueOf, OperatorFunction, queueScheduler, Subject, take
} from "rxjs";
import {first, mergeMap, tap} from 'rxjs/operators';

import { Logger }                from '@/util/logger';
import { Project }               from '@/models/project';
import { ProjectHistoryItem }    from '@/models/project.history.item';
import { User }                  from '@/models/user';
import { errorAsText }           from '@/util/util';
import { Blob, BlobType }        from '@/models/blob';
import { ProjectFactory }        from '@/models/project.factory';
import { AuthenticationService } from './authentication.service';
import { BlobService }           from './blob.service';
import { AlertService }          from './alert.service';
import { JobWatcherService }     from './job.watcher.service';
import { ProjectSummary }        from '@/models/project/project.summary';

const MAX_HISTORY: number = 10;

@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class ProjectService
 *
 * provides access to project and the current project/history for the logged-in user.
 *
 * per user project data:
 *
 * /users/<userid>/projects/<name>.json
 *   {serialized version of <name> project}
 *
 * /users/<userid>/projects/recent.json
 *   {
 *     current: null|key
 *     recent: [
 *       {
 *         key: /users/<userid>/projects/<name>.json
 *         name: <name>
 *       },
 *       ...
 *     ]
 *   }
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2022
 *
 */
export class ProjectService {

  /* the currently active project */

  private currentProjectSubject:          BehaviorSubject<Project>;
  private readonly currentProject:        Observable<Project>;

  /* the current project history */

  private historySubject:                 BehaviorSubject<ProjectHistoryItem[]>;
  private readonly historyObservable:     Observable<ProjectHistoryItem[]>;

  /** the save request queue */
  private saveQueue: Subject<Project|null>= new Subject<Project|null>();
  private static saveRequestId: number    = 0;

  /** the current logged-in user, there has to always be a logged-in user */
  private _user: User                     = null;

  /** the project history for this user, front is most recently used */
  private _history: ProjectHistoryItem[] = [];

  /** the current project for this user. */
  private _project: Project              = null;

  /** clean up when service is destroyed */
  ngOnDestroy(): void {

    Logger.debug('[ProjectService] destructing...');

  }

  /** standard constructor */
  constructor(
    private     auth: AuthenticationService,
    private  blobber: BlobService,
    private   alerts: AlertService,
    private  watcher: JobWatcherService,
    private     http: HttpClient
  ) {

    let ps: ProjectService = this;

    Logger.debug('[ProjectService] constructing...');

    this.currentProjectSubject = new BehaviorSubject<Project>(null);
    this.currentProject        = this.currentProjectSubject.asObservable();

    this.historySubject        = new BehaviorSubject<ProjectHistoryItem[]>(null);
    this.historyObservable     = this.historySubject.asObservable();

    /* set our save queue */

    Logger.debug('[ProjectService] creating save queue...');

    ps.saveQueue.pipe(this.concatLatest((input: Project) => from(this.doSave(input)))).subscribe({

      next: (result: any): void => {

        Logger.debug('[ProjectService] (saveQueue) latest save completed: ', result);
      },

      error: (err: any): void => {

        ps.alerts.error(errorAsText(err), {autoClose: true});

        Logger.error('[ProjectService] (saveQueue) could not queue save: ', err);
      }

    });

    /* good to go! */

    Logger.debug('[ProjectService] ready.');
  }

  /** fetch the current user */
  public user(): User {

    return this._user;
  }

  /** fetch any headers we need to send along with our request */
  headers() : HttpHeaders {

    const user: User = this.auth.userOfSession;

    if (!user) {

      /* no user logged in */

      return null;
    }

    return new HttpHeaders().set('Authorization', `Bearer ${user.token}`);
  }

  /** set the current user */
  public setUser(user: User): ProjectService {

    this._user    = user;
    this._history = [];

    /* make sure any subscribers on 'current project' clear themselves */

    this.historySubject.next(this._history);

    return this;
  }

  /** fetch the current project */
  public project(): Project {

    return this._project;
  }

  /** helper to convert history into JSON friendly object list */
  private serializeHistory(): any[] {

    let data: any;

    if (this._project !== null) {

      data = {
        current: this._project.key,
        recent: []
      };

    } else {

      data = {
        current: null,
        recent: []
      };
    }

    for (const item of this._history) {

      data.recent.push({
        key:  item.key,
        name: item.name
      });
    }

    return data;
  }

  /** set the current project */
  public setProject(project: Project): ProjectService {

    let ps: ProjectService = this;

    ps._project = project;

    if (ps._project === null) {

      Logger.debug('[ProjectService] clearing project ');

    } else {

      Logger.debug('[ProjectService] updating project history: ', ps._project);

      /* update the history; keep limited history, and make sure this one...is at the front */

      if (ps._history.length >= MAX_HISTORY) {

        ps._history.pop();
      }

      ps._history = ps._history.filter((x: ProjectHistoryItem): boolean => x.key !== ps._project.key);
      ps._history.unshift(new ProjectHistoryItem(ps._project.name, ps._project.key));

    }

    /* save the updated history */

    const file: string = ps.historyFileName();
    let data: any[]    = ps.serializeHistory();

    ps.currentProjectSubject.next(null); /* clear project first */

    Logger.debug(`[ProjectService] saving project ${file}`, data);

    ps.blobber.update(file, JSON.stringify(data), 'latest', true)
      .pipe(first())
      .subscribe({
        next: (): void => {

          Logger.debug('[ProjectService] project history saved.');

          /* publish the change of project */

          Logger.debug('[ProjectService] setting project: ', ps._project);

          ps.currentProjectSubject.next(ps._project);

          ps.historySubject.next(ps._history);

        },
        error: (err): void => {

          Logger.error('[ProjectService] can not save project history: ', errorAsText(err));

          ps.alerts.error(errorAsText(err), {autoClose: true});

        }

      });

    return this;
  }

  /** fetch the project history of this user */
  public history(): ProjectHistoryItem[] {

    return this._history;
  }

  /** set the project history for this user */
  public setHistory(history: ProjectHistoryItem[]): ProjectService {

    this._history = history;


    return this;
  }

  /** fetch the list of my own projects */
  public myProjects(): Observable<any> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.get<any>(`/backend/my/projects`, {
      headers,
    });

  }

  /** fetch the user's project history...but as an Observable */
  public get observeHistory(): Observable<ProjectHistoryItem[]> {

    return this.historyObservable;
  }

  /** fetch the user's active project...but as an Observable */
  public get observeProject(): Observable<Project> {

    return this.currentProject;
  }

  /** fetch the location of the project history file */
  historyFileName(): string {

    /* can only have a user project history file if we have a user! */

    if (!this._user) {

      return null;
    }

    return `/users/${this._user.username}/projects/recent.json`;
  }

  /** when the project changes, make sure the copy of it in "recent history" is also updated. */
  saveRecent(): ProjectService {

    let ps: ProjectService = this;

    Logger.debug('[ProjectService] updating project history (project changed)');

    const file: string = ps.historyFileName();
    let data: any[]    = ps.serializeHistory();

    Logger.debug(`[ProjectService] updating ${file}`, data);

    ps.blobber.update(file, JSON.stringify(data), 'latest', true)
      .pipe(first())
      .subscribe(
        () => {

          Logger.debug('[ProjectService] project history saved.');

        },
        error => {

          Logger.error('[ProjectService] can not save project history: ', error);

          ps.alerts.error(errorAsText(error), {autoClose: true});

        });

    return ps;
  }

  /** helper to reset the history file (usually when it doesn't exist already */
  initHistoryFile(): ProjectService {

    let ps: ProjectService = this;

    Logger.debug('[ProjectService] setting (new) history file...');

    const history = {
      current: null,
      recent: []
    };

    const file: string = ps.historyFileName();
    const data: string = JSON.stringify(history);

    Logger.debug(`[ProjectService] creating ${file}`, data);

    ps.blobber.create(file, BlobType.Project, data, 'recent.json', 'user project history', true)
      .pipe(first())
      .subscribe(
        () => {

          ps._history        = [];

          ps.currentProjectSubject.next(null);
          ps.historySubject.next(ps._history);
        },
        error => {

          Logger.error('[ProjectService] can not create project history file: ', error);

          ps.alerts.error(errorAsText(error), {autoClose: true});

        });

    return ps;
  }

  /** load up the most recent project */
  lastActive(): ProjectService {

    /* ensure we have a "recent history" file */

    let ps: ProjectService = this;
    const file: string     = ps.historyFileName();

    Logger.debug(`[ProjectService] last active... ${file}`);

    ps.blobber.contents(file)
      .pipe(first())
      .subscribe(
        text => {

          if (!text) {

            /* no history yet, create the history file the first time */

            ps.initHistoryFile();

          } else {

            const data: any = JSON.parse(text as string);

            Logger.debug('[ProjectService] recent history meta: ' + data);

            /* extract the history  */

            ps._history = [];

            for (const item of data.recent) {

              ps._history.push(new ProjectHistoryItem(item.name, item.key));
            }

            /* clear the current project */

            ps._project = null;

            ps.currentProjectSubject.next(ps._project);
            ps.historySubject.next(ps._history);

            /* set current project...if there is one, already current, so we don't update history */

            if (data.current) {

              const key: string = data.current;

              ps.blobber.contents(key)
                .pipe(first())
                .subscribe(
                  projectJSON => {

                    /* load up a new project */

                    ps._project = ProjectFactory.fromJSON(projectJSON as string) as Project;

                    Logger.debug('[ProjectService] known jobs: ', ps.watcher);
                    Logger.debug('[ProjectService] switching to project: ', ps._project);

                    ps.currentProjectSubject.next(ps._project);
                    ps.historySubject.next(ps._history);

                    Logger.debug('[ProjectService] recent projects: ', ps._history);
                  },
                  error => {

                    Logger.error('[ProjectService] can not fetch project content: ', error);

                    ps.alerts.error(errorAsText(error), {autoClose: true});

                  });

            }
          }
        },
        () => {

          /* no history yet, create the history file the first time */

          ps.initHistoryFile();
        });

    return ps;
  }

  /** seed a project with an existing project */
  cloneProject(): ProjectService  {

    return this;
  }

  /** fetch the current project folder path (key) */
  projectFolder(): string {

    if (!this._user) {

      return null;
    }

    if (!this._project) {

      return null;
    }

    return this.currentProjectSubject.value.defaultProjectFolder(this._user.username, this._project.name);
  }

  /*
   * custom version of concatMap() that only keeps a queue size of 1, we can use
   * this for our save queue, so we don't do multiple saves of the same stuff.
   *
   *   ref: https://stackoverflow.com/questions/74315746/how-to-achieve-an-rxjs-operator-like-concatmap-with-a-queue-size-of-1
   *   ref: https://medium.com/@jan.benscheid/mutual-exclusion-using-rxjs-f5f3a11237b7
   *
   * the core problem with saving is that it can get called all over the place, any time
   * a project tree node is touched/updated, but when we "save" we're handing off
   * to asynchronous functions (REST calls), so while a save is happening, other
   * save requests can come in.  We don't want to just block and wait, because if we
   * queue up 3 requests to save while waiting for the outstanding one to complete,
   * we don't want to save 3 more times...we just want to do another save to catch
   * any changes that happened, while we were saving.
   *
   * This is the critical part, we want to avoid all the extra "overlapping" saves,
   * but still do a save when it really is necessary.  So we "queue" save requests
   * through concatMap()...but with a queue size of one, or said another way "just
   * queue the latest request".
   *
   */
  concatLatest<T, O extends Observable<unknown>>(
    project: (value: T, index: number) => O
  ): OperatorFunction<T, ObservedValueOf<O>> {
    let subj: BehaviorSubject<O> | null = null;
    let latestObservable: O = null;

    return mergeMap((val, i) => {
      latestObservable = defer(() =>
        project(val, i).pipe(
          tap({
            complete: (): void => {
              if (latestObservable) {
                subj.next(latestObservable);
                latestObservable = null;
              } else {
                subj.complete();
                subj = null;
              }
            },
          })
        )
      ) as O;

      if (subj) {
        return EMPTY;
      }

      subj = new BehaviorSubject(latestObservable);

      return subj.pipe(mergeAll());
    });
  }

  doSave(project?: Project): Observable<any> {

    let ps: ProjectService = this;

    Logger.debug('[ProjectService] (doSave) project saving starts...');

    const oldProject: Project = (project ? project : ps._project);

    if (!oldProject) {

      /* nothing to save */

      Logger.debug('[ProjectService] (doSave) nothing to save.');

      return EMPTY;
    }

    Logger.debug('[ProjectService] (doSave) starting save on:', oldProject);

    /* try to serialize project to JSON ... */

    const text: string = oldProject.serialize();
    let    obj: any    = null;

    /* check that the serialized JSON is legit... */

    try {

      obj = JSON.parse(text);

      if ((obj === null) || (typeof obj === 'undefined')) {

        Logger.error('[ProjectService] (doSave) project JSON did not serialize to valid JSON.');

        /* abort */

        return EMPTY;
      }

    } catch (e) {

      Logger.error('[ProjectService] (doSave) project has invalid JSON, can not save: ', e);

      /* abort */

      return EMPTY;
    }

    Logger.debug(`[ProjectService] (doSave) serialized, saving valid JSON ${oldProject.key}`, obj);

    const a: Observable<any> = ps.blobber.update(oldProject.key, text, 'latest', true);

    return a.pipe(first());
  }

  /** save the active project to disk (if there is one) */
  saveProject(project?: Project): ProjectService {

    let ps: ProjectService = this;

    ProjectService.saveRequestId++;

    if (project) {

      if (!project.dirty) {

        Logger.debug(`[ProjectService] project save requested, but not yet modified, skipping save.`);

        return ps;
      }

      Logger.debug(`[ProjectService] queueing save of project (${ProjectService.saveRequestId}): `, project);

      /*
       * push the request into the save queue.  We use our own concatLatest() version
       * of concatMerge to ensure we only ever have one outstanding save request.  This
       * avoids doing multiple saves that get queued up while waiting for asynchronous
       * REST requests to finish.
       *
       */

      ps.saveQueue.next(project);
    }

    return ps;
  }

  /** open an existing project */
  openProject(project: Project): ProjectService {

    Logger.debug('[ProjectService] swapping to project:', project);

    if (project) {

      /* save the existing project */

      this.saveProject(this._project);
    }

    /* open the new one... */

    this.setProject(project);

    return this;
  }


  /** close the active project, if there is one */
  closeProject(): ProjectService {

    this.openProject(null);

    return this;
  }

  /** delete the current project */
  deleteProject(): ProjectService {

    let ps: ProjectService = this;

    if (!ps._project) {

      /* nothing to delete */

      return ps;
    }

    /* save a copy of existing project, so we can delete it */

    const oldProject = ps._project;

    Logger.debug('[ProjectService] deleting project: ', oldProject);

    /* clear the project window */

    ps.closeProject();

    /* remove it from the recent history */

    const file: string = ps.historyFileName();

    ps._history        = ps._history.filter(x => x.key !== oldProject.key);

    const data: any    = ps.serializeHistory();

    Logger.debug(`[ProjectService] deleting project ${file}`, data);

    ps.blobber.update(file, JSON.stringify(data), 'latest', true)
      .pipe(first())
      .subscribe(
        () => {

          ps.historySubject.next(ps._history);

          Logger.debug('[ProjectService] project history saved.');

          /* delete the project we don't need anymore... */

          oldProject.delete();

        },
        error => {

          Logger.error('[ProjectService] can not save project history: ', error);

          ps.alerts.error(errorAsText(error), {autoClose: true});

        });


    return ps;
  }

  /** rename a project */
  renameProject(newName: string): ProjectService {

    let ps: ProjectService = this;

    if (!ps._project) {

      /* nothing to delete */

      return ps;
    }

    /* save a copy of existing project, so we can rename it */

    const oldProject: Project = ps._project;

    const     newKey: string  = oldProject.defaultProjectFile(ps._user.username, newName);
    const     oldKey: string  = oldProject.key;

    Logger.debug('[ProjectService] (rename) renaming project: ', oldProject);

    /* update the recent history, remove the old project path, and add the new path */

    const newHistory: ProjectHistoryItem[] = [];

    /* remove the old name ... */

    /* add the new name ... */

    newHistory.push(new ProjectHistoryItem(newName, newKey));

    for (const item of ps._history.filter(x => x.key !== oldKey)) {

      newHistory.push(new ProjectHistoryItem(item.name, item.key));
    }

    ps._history = newHistory;

    /* now update the recent file contents ... */

    const file: string = ps.historyFileName();
    const data: any    = ps.serializeHistory();

    /* save the update ... */

    Logger.debug('[ProjectService] renaming project: ', data);

    ps.blobber.update(file, JSON.stringify(data), 'latest', true)
      .pipe(first())
      .subscribe(
        () => {

          /* publish the new "recent projects" ... */

          Logger.debug(`[ProjectService] (rename) publishing new history: `, ps._history);

          ps.historySubject.next(ps._history);

          /* rename the project internally (shift any relative elements)... */

          oldProject.move(newKey, newName);

        },
        error => {

          Logger.error('[ProjectService] (rename) can not save project history: ', error);

          ps.alerts.error(errorAsText(error), {autoClose: true});

        });

    return ps;
  }

  /** new project from scratch */
  newProject(project: Project): ProjectService {

    let ps: ProjectService = this;

    Logger.debug('[ProjectService] creating new project:', project);

    /* save new project blob */

    const  key: string = project.defaultProjectFile(ps._user.username, project.name);
    let   data: string;

    project.key        = key;
    project.instance   = 'latest';

    data = project.serialize();

    ps.blobber.create(key, BlobType.Project, data, `project-${project.name}.json`, 'project', true)
      .pipe(first())
      .subscribe(
        created => {

          /* link the blob to the project */

          project.blob = created as Blob;

          Logger.debug('[ProjectService] new project saved: ', project.key);

          /* set the project */

          ps.openProject(project);

        });

    return ps;
  }

}
